import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { isHoldingScrollAnchor } from '../lib/scrollAnchor'

const THRESHOLD = 8
const TOP_ZONE = 40

// The state NavBar computes, shared so other floating chrome (Home's FABs) tucks
// away on exactly the same scroll instead of running its own, subtly different rule.
let published = false
const subscribers = new Set<() => void>()
function publish(value: boolean) {
  if (value === published) return
  published = value
  subscribers.forEach(fn => fn())
}

/** Whether the bottom nav is currently compacted — read-only, for chrome that moves with it. */
export function useIsNavCompact(): boolean {
  return useSyncExternalStore(
    fn => {
      subscribers.add(fn)
      return () => subscribers.delete(fn)
    },
    () => published,
  )
}

// Instagram-style: the bottom nav compacts while the user scrolls down and comes
// back on any scroll up or near the top. Listens in the capture phase so inner
// scrollers (the schedule grid) count, not just the window. Mirrors the state to
// <html data-nav-compact> so BottomChrome can hold its published height steady —
// otherwise page padding would shrink mid-scroll and jolt the content.
export function useNavCompact(enabled = true): { compact: boolean; expand: () => void } {
  // location.key is unique per navigation, even back to a page already visited.
  const { key: navKey } = useLocation()
  // Keyed to the navigation it was set on, so every arrival — including returning
  // to a scrolled-down tab — starts expanded.
  const [compactOn, setCompactOn] = useState<string | null>(null)
  const compact = enabled && compactOn === navKey
  const navKeyRef = useRef(navKey)
  // Per scroller, the scrollTop where the state last changed — measuring from there
  // (not from the previous event) lets a slow scroll's small steps add up.
  // Reset per route so a stale position from the previous page never reads as a
  // big downward scroll.
  const anchorRef = useRef(new WeakMap<EventTarget, number>())
  // Only scrolling after real input on this route compacts the nav: scroll
  // restoration on returning to a tab lands before any touch, so it doesn't count,
  // while a flick's momentum (after the finger lifts) still does.
  const touchedRef = useRef(false)
  useEffect(() => {
    navKeyRef.current = navKey
    anchorRef.current = new WeakMap()
    touchedRef.current = false
  }, [navKey])

  useEffect(() => {
    const onInput = () => {
      touchedRef.current = true
    }
    const onScroll = (e: Event) => {
      const el = e.target === document ? document.scrollingElement : (e.target as Element | null)
      if (!el) return
      const anchors = anchorRef.current
      const top = el.scrollTop
      const anchor = anchors.get(el)
      // Content shifting under a held scroll anchor isn't the user scrolling.
      if (anchor === undefined || !touchedRef.current || isHoldingScrollAnchor()) {
        anchors.set(el, top)
        return
      }
      if (top < TOP_ZONE) setCompactOn(null)
      else if (top > anchor + THRESHOLD) setCompactOn(navKeyRef.current)
      else if (top < anchor - THRESHOLD) setCompactOn(null)
      else return
      anchors.set(el, top)
    }
    const opts = { capture: true, passive: true }
    const inputs = ['touchmove', 'wheel', 'keydown'] as const
    inputs.forEach(t => document.addEventListener(t, onInput, opts))
    document.addEventListener('scroll', onScroll, opts)
    return () => {
      inputs.forEach(t => document.removeEventListener(t, onInput, opts))
      document.removeEventListener('scroll', onScroll, opts)
    }
  }, [])

  useEffect(() => {
    const root = document.documentElement
    if (compact) root.dataset.navCompact = ''
    else delete root.dataset.navCompact
    publish(compact)
    return () => {
      delete root.dataset.navCompact
      publish(false)
    }
  }, [compact])

  // A tap on the compact pill brings it back, even on the tab already showing.
  const expand = useCallback(() => setCompactOn(null), [])

  return { compact, expand }
}
